import { reactive, ref } from 'vue'
import { githubApi } from '@/api/github'
import { withAsyncState } from '@/lib/asyncState'
import { CODE_GRAPH_STATUS } from '@/lib/reviewSettingsLabels'
import type { CodeGraph } from '@/types/models'

// Singleton, same shape as useRepoRules.ts's scan state: a reactive Record
// keyed by repository-settings id, so a repo's code graph status survives
// navigating away from and back to its settings page within a session. Kept
// separate from useRepoRules rather than folded in — a code graph is an
// unrelated concept from custom rules/scans, just modeled the same way.
const graphsByRepoSettings = reactive<Record<string, CodeGraph>>({})
const loading = ref(false)
const submitting = ref(false)
const error = ref<string | null>(null)

export function useCodeGraph() {
  async function fetchGraph(installationId: string, repositorySettingsId: string): Promise<void> {
    if (!repositorySettingsId) return
    await withAsyncState(
      error,
      'Failed to load code graph.',
      async () => {
        graphsByRepoSettings[repositorySettingsId] = await githubApi.getCodeGraph(installationId, repositorySettingsId)
      },
      loading,
    )
  }

  function getGraph(repositorySettingsId: string): CodeGraph | null {
    return graphsByRepoSettings[repositorySettingsId] ?? null
  }

  function isGraphGenerating(repositorySettingsId: string): boolean {
    const graph = getGraph(repositorySettingsId)
    return graph?.status === CODE_GRAPH_STATUS.QUEUED || graph?.status === CODE_GRAPH_STATUS.RUNNING
  }

  async function triggerGraph(installationId: string, repositorySettingsId: string): Promise<void> {
    await withAsyncState(
      error,
      'Failed to start code graph generation.',
      async () => {
        graphsByRepoSettings[repositorySettingsId] = await githubApi.triggerCodeGraph(installationId, repositorySettingsId)
      },
      submitting,
    )
  }

  // Called on logout so the next session doesn't briefly show the previous
  // user's code graph status before its own fetch resolves.
  function reset(): void {
    for (const key of Object.keys(graphsByRepoSettings)) delete graphsByRepoSettings[key]
    loading.value = false
    submitting.value = false
    error.value = null
  }

  return {
    loading,
    submitting,
    error,
    fetchGraph,
    getGraph,
    isGraphGenerating,
    triggerGraph,
    reset,
  }
}
